/**
 * 단일 InvoiceFile 의 Record 편집 모달.
 *
 * 정책:
 *   - csv_data 는 부분 머지(변경된 키만 전송).
 *   - 반려 상태는 별도 토글 + 사유 입력.
 *   - 메모 필수 아님.
 *   - pending_extraction 이 있으면 편집 차단 (모달 자체 열리지 않도록 호출측에서 가드).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Maximize2,
  Minimize2,
  Sparkles,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/features/jobs/format";
import { ApiError } from "@/lib/api/client";
import type { CorrectionStatus } from "@/lib/api/corrections";
import type { FieldBbox, InvoiceFileOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";

import { autoFixChanges } from "./automationStatusUtil";
import CorrectionSuggestionModal from "./corrections/CorrectionSuggestionModal";
import { useCorrectionList, useCorrectionStatusWatcher } from "./corrections/useCorrections";
import { localizeAiText } from "./aiText";
import DiffReviewPanel from "./DiffReviewPanel";
import { EDITABLE_FIELDS, type EditableFieldKey } from "./fieldLabels";
import PdfPreviewPanel from "./PdfPreviewPanel";
import ReviewStatusBadge from "./ReviewStatus";
import { computeReviewSignals, fieldSignalMap } from "./reviewSignals";
import { canConfirmReview, deriveReviewState } from "./reviewStatusUtil";
import { useEditRecord } from "./useEditRecord";
import {
  useClearAutoFixChecked,
  useClearReviewComplete,
  useClearReviewConfirmed,
  useSetAutoFixChecked,
  useSetReviewComplete,
  useSetReviewConfirmed,
} from "./useReviewComplete";
import { useResizableSplit } from "./useResizableSplit";

// 필드 키 → 표시 라벨 i18n 키 조회 (확인 필요 패널에서 사용).
const FIELD_LABEL_KEY_MAP: Record<EditableFieldKey, string> = Object.fromEntries(
  EDITABLE_FIELDS.map((m) => [m.key, m.labelI18nKey]),
) as Record<EditableFieldKey, string>;

// "확인 필요" 패널 접힘 여부 — 뷰어 전역(localStorage) 편의 토글 키.
// 각 작업/레코드가 아니라 뷰어 자체에서 켜고 끈다. 영수증을 이동해도 유지되어
// 패널 높이 변화로 '검수 완료' 체크 위치가 흔들리는 것을 방지한다.
const REVIEW_PANEL_COLLAPSE_KEY = "editRecord.reviewPanelCollapsed";

// 좌측 패널의 기본 비율 = 7 / (7+5) ≒ 58.33%.
// 좌측이 PDF 미리보기라 우선순위가 높고, 우측 폼은 최소 25% 확보.
const SPLIT_DEFAULT_PCT = 58.33;
const SPLIT_MIN_PCT = 30;
const SPLIT_MAX_PCT = 75;
const SPLIT_STORAGE_KEY = "editRecordModal:splitLeftPct";
const MAXIMIZE_STORAGE_KEY = "editRecordModal:maximized";

interface Props {
  jobId: string;
  file: InvoiceFileOut;
  onClose: () => void;
  /**
   * M13 — paging 지원 (옵셔널). RecordTable 이 현재 filter 결과를 기준으로
   * ``current / total`` 과 ``onPrev`` / ``onNext`` 를 주입한다.
   *
   * 정책 (Q1=separate, Q2=block_with_hint, Q3=filtered, Q4=disable):
   *   - 저장(submit)과 paging 은 별개 버튼.
   *   - ``hasAnyChange === true`` 이면 prev/next 비활성화 + 툴팁.
   *   - 첫/마지막 파일이면 prev/next 비활성화.
   */
  position?: { current: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
  /** M15 — 수정제안 제출 성공 시 (fileId, requestId) 통지 → 행 진행 표시용. */
  onCorrectionSubmitted?: (fileId: string, requestId: string) => void;
  /**
   * 관리자 감독(읽기전용) 모드. true 면 모든 쓰기 액션(편집·검수·확정·재추출/
   * 수정제안)을 잠그고 입력을 비활성화한다. 백엔드도 비소유자 쓰기를 404 로 막지만,
   * 혼란(404 토스트) 방지를 위한 UX 가드.
   */
  readOnly?: boolean;
}

/** CSV 값 → string (null/undefined 는 빈 문자열). */
function toStr(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

export default function EditRecordModal({
  jobId,
  file,
  onClose,
  position,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  onCorrectionSubmitted,
  readOnly = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const record = file.record;

  // M21 #7 — 검수 완료 체크 + 최종 AI 검토 상태.
  const setReviewMutation = useSetReviewComplete();
  const clearReviewMutation = useClearReviewComplete();
  const reviewState = deriveReviewState(record);
  const reviewChecked = (record?.review_status ?? "none") !== "none";
  const reviewBusy = setReviewMutation.isPending || clearReviewMutation.isPending;
  const toggleReview = useCallback(() => {
    if (reviewBusy || !record || readOnly) return;
    if (reviewChecked) {
      clearReviewMutation.mutate({ jobId, fileId: file.id });
    } else {
      setReviewMutation.mutate({ jobId, fileId: file.id });
    }
  }, [
    reviewBusy,
    record,
    readOnly,
    reviewChecked,
    clearReviewMutation,
    setReviewMutation,
    jobId,
    file.id,
  ]);

  // 2차 검수 완료 (AI 지적 확인 후 최종 사인오프).
  const setConfirmMutation = useSetReviewConfirmed();
  const clearConfirmMutation = useClearReviewConfirmed();
  const confirmChecked = record?.review_status === "confirmed";
  const confirmAvailable = reviewChecked && canConfirmReview(record);
  const confirmBusy = setConfirmMutation.isPending || clearConfirmMutation.isPending;
  const toggleConfirm = useCallback(() => {
    if (confirmBusy || !record || !confirmAvailable || readOnly) return;
    if (confirmChecked) {
      clearConfirmMutation.mutate({ jobId, fileId: file.id });
    } else {
      setConfirmMutation.mutate({ jobId, fileId: file.id });
    }
  }, [
    confirmBusy,
    record,
    confirmAvailable,
    readOnly,
    confirmChecked,
    clearConfirmMutation,
    setConfirmMutation,
    jobId,
    file.id,
  ]);

  // 자동 검수가 고친 값 — PDF 와 대조할 칸과 "고친 값 확인함"(목록의 "확인 권장" 끄기).
  const autoFix = autoFixChanges(record?.automation);
  const autoFixByKey = new Map(autoFix.map((change) => [change.key, change]));
  const autoFixChecked = !!record?.automation?.checked_at;
  const setAutoFixMutation = useSetAutoFixChecked();
  const clearAutoFixMutation = useClearAutoFixChecked();
  const autoFixBusy = setAutoFixMutation.isPending || clearAutoFixMutation.isPending;
  const toggleAutoFixChecked = () => {
    if (autoFixBusy || readOnly) return;
    const mutation = autoFixChecked ? clearAutoFixMutation : setAutoFixMutation;
    mutation.mutate({ jobId, fileId: file.id });
  };
  const emptyValue = t("records.automation.autoFix.empty");

  // 16 필드의 편집 가능한 string 값. 대리인명은 납품 값(통화 표시 제외)으로 보여 준다 —
  // 손대지 않으면 변경으로 보지 않아 저장 값(마스터의 계좌 구분)은 그대로 남는다.
  const initialCsv = useMemo<Record<EditableFieldKey, string>>(() => {
    const csv = record?.csv_data ?? {};
    const out = {} as Record<EditableFieldKey, string>;
    for (const meta of EDITABLE_FIELDS) {
      out[meta.key] = toStr((csv as Record<string, unknown>)[meta.key]);
    }
    if (record?.delivered_agent_name) out.agent_name = record.delivered_agent_name;
    return out;
  }, [record]);

  const [csv, setCsv] = useState<Record<EditableFieldKey, string>>(initialCsv);
  const [isRejected, setIsRejected] = useState<boolean>(record?.is_rejected ?? false);
  const [rejectionReason, setRejectionReason] = useState<string>(record?.rejection_reason ?? "");
  const [memo, setMemo] = useState<string>("");

  // M15 — 수정제안 모달 (이 모달 위에 띄움)
  const [showCorrection, setShowCorrection] = useState(false);
  // 편집창 최대화 여부 — 브라우저에 유지(스플리터와 동일 패턴).
  // 기본값은 최대화 ON. 사용자가 명시적으로 복원("0")한 경우에만 해제.
  const [maximized, setMaximized] = useState<boolean>(() => {
    try {
      return localStorage.getItem(MAXIMIZE_STORAGE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggleMaximized = useCallback(() => {
    setMaximized((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MAXIMIZE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // 저장 실패는 무시 (프라이빗 모드 등).
      }
      return next;
    });
  }, []);
  // #6 — 수정제안 제출 후에도 편집 모달을 유지하고 "수정중" 상태를 보여준다.
  //   correctionRequestId: 진행 중인 재추출 요청 id (없으면 null).
  //   lastCorrectionStatus: 최근 종료(터미널) 상태 — 변경없음/실패 안내용.
  //   showReview: 재처리 결과(diff) 검토 오버레이 표시 여부.
  const [correctionRequestId, setCorrectionRequestId] = useState<string | null>(null);
  const [lastCorrectionStatus, setLastCorrectionStatus] = useState<CorrectionStatus | null>(null);
  const [showReview, setShowReview] = useState(false);

  // M11 — 양방향 강조: focus pin + hover
  const [activeFieldKey, setActiveFieldKey] = useState<EditableFieldKey | null>(null);
  const [hoveredFieldKey, setHoveredFieldKey] = useState<EditableFieldKey | null>(null);
  // "확인 필요" 패널 접힘 상태 (뷰어 전역 — localStorage 로 영수증 이동에도 유지).
  const [reviewPanelCollapsed, setReviewPanelCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(REVIEW_PANEL_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleReviewPanel = useCallback(() => {
    setReviewPanelCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(REVIEW_PANEL_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // localStorage 비활성/Quota 등은 무시
      }
      return next;
    });
  }, []);
  // M11-F5 — 매칭 실패(null) 필드 회색 점 배지 표시용.
  // 백엔드 csv_bbox 를 그대로 사용 (B7-c: dev mock 제거 후 단일 데이터 소스).
  const effectiveBboxes: Record<string, FieldBbox | null> | null = record?.csv_bbox ?? null;

  // #3 — 값 기반 의심 신호(S2~S4). 편집 중 값(csv) 기준으로 실시간 재계산되어
  // 사용자가 값을 채우면 강조가 사라진다. 강조 대상 필드 → 메시지 키 맵으로 변환.
  const reviewSignals = useMemo(
    () => computeReviewSignals(csv, file.original_name),
    [csv, file.original_name],
  );
  const signalFieldMap = useMemo(() => fieldSignalMap(reviewSignals), [reviewSignals]);

  // #2/#4 — 저신뢰(원본 불일치) 필드 목록. 備考(remarks)는 AI 소견이라 PDF 대조
  // 대상이 아니므로 제외한다(오탐 방지). effectiveBboxes 가 null 이면 매칭 미수행.
  const lowConfFieldKeys = useMemo<EditableFieldKey[]>(() => {
    if (effectiveBboxes === null) return [];
    const keys: EditableFieldKey[] = [];
    for (const meta of EDITABLE_FIELDS) {
      if (meta.key === "remarks") continue;
      if (!Object.prototype.hasOwnProperty.call(effectiveBboxes, meta.key)) continue;
      const entry = effectiveBboxes[meta.key];
      if (entry === undefined) continue;
      if (entry === null || entry.confidence < 1) keys.push(meta.key);
    }
    return keys;
  }, [effectiveBboxes]);

  // #4 — AI 소견(검증/금액 분석 경고). 신규 레코드는 csv_data.review_notes(list),
  // 구 레코드는 반려가 아닌데 remarks 에 경고가 남아있는 경우를 소견으로 호환 처리.
  const reviewNotes = useMemo<string[]>(() => {
    const lang = i18n.language;
    const notes: string[] = [];
    const push = (s: string) => {
      const loc = localizeAiText(s, lang);
      if (loc && !notes.includes(loc)) notes.push(loc);
    };
    const raw = (record?.csv_data ?? {}) as Record<string, unknown>;
    const rn = raw.review_notes;
    if (Array.isArray(rn)) {
      for (const n of rn) {
        const s = String(n).trim();
        if (s) push(s);
      }
    }
    if (!record?.is_rejected) {
      const rem = String(raw.remarks ?? "").trim();
      if (rem) {
        for (const part of rem.split(";")) {
          const s = part.trim();
          if (s) push(s);
        }
      }
    }
    return notes;
  }, [record, i18n.language]);

  const hasReviewPanel =
    reviewSignals.length > 0 || lowConfFieldKeys.length > 0 || reviewNotes.length > 0;

  // M11-F6 — bbox 클릭 시 해당 필드의 input 으로 스크롤/포커스 점프.
  // Map 으로 관리: ref callback 으로 mount 시 등록, unmount 시 자동 정리됨.
  const inputRefs = useRef<Map<EditableFieldKey, HTMLInputElement | null>>(new Map());
  const handleBboxClick = useCallback((key: EditableFieldKey) => {
    setActiveFieldKey(key);
    const el = inputRefs.current.get(key);
    if (!el) return;
    // preventScroll: true → focus 가 일으키는 jump 를 차단하고,
    // 아래 scrollIntoView 의 smooth 애니메이션만 적용되게 함.
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // record 변경 시 초기화
  useEffect(() => {
    setCsv(initialCsv);
    setIsRejected(record?.is_rejected ?? false);
    setRejectionReason(record?.rejection_reason ?? "");
    setMemo("");
  }, [initialCsv, record]);

  // ESC 닫기 — 단, 수정제안 모달이 위에 떠 있으면 그 모달이 Esc 를 처리하므로
  // 편집 모달은 무시한다 (그리기 취소/수정제안 닫기가 편집 모달까지 닫는 것 방지).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showCorrection) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, showCorrection]);

  const editMutation = useEditRecord();

  const split = useResizableSplit({
    storageKey: SPLIT_STORAGE_KEY,
    defaultPct: SPLIT_DEFAULT_PCT,
    min: SPLIT_MIN_PCT,
    max: SPLIT_MAX_PCT,
  });

  // 변경된 csv 필드만 추출
  const changedCsv = useMemo<Record<string, string>>(() => {
    const diff: Record<string, string> = {};
    for (const meta of EDITABLE_FIELDS) {
      const next = csv[meta.key];
      const prev = initialCsv[meta.key];
      if (next !== prev) diff[meta.key] = next;
    }
    return diff;
  }, [csv, initialCsv]);

  const rejectionChanged =
    isRejected !== (record?.is_rejected ?? false) ||
    (isRejected && rejectionReason !== (record?.rejection_reason ?? ""));

  const hasAnyChange = Object.keys(changedCsv).length > 0 || rejectionChanged;

  // 금액 필드 형식 가드 (REQ-014) — 콤마/공백/문자 등 비정상 표기는 저장 불가.
  //   납품 금액은 소수점 '.' + 숫자만 유효(빈값 허용). 사용자가 직접 정리하도록
  //   하이라이트 + 저장 차단(자동 변환 대신 명시적 수정 유도).
  const invalidAmountKeys = useMemo<EditableFieldKey[]>(() => {
    const keys: EditableFieldKey[] = [];
    for (const meta of EDITABLE_FIELDS) {
      if (meta.type !== "amount") continue;
      const raw = String(csv[meta.key] ?? "").trim();
      if (raw === "") continue;
      if (!/^-?\d+(\.\d+)?$/.test(raw)) keys.push(meta.key);
    }
    return keys;
  }, [csv]);
  const hasInvalidAmount = invalidAmountKeys.length > 0;

  // 키보드 화살표(←/→)로 이전/다음 안건(레코드) 이동.
  //  - PDF 페이지 넘김은 PdfPreviewPanel 이 PageUp/PageDown 으로 담당(분리)
  //    → 영수증 페이지 수와 무관하게 ←/→ 는 항상 안건 이동에 쓰인다.
  //  - 절대 금지: 수정제안 작성 중(showCorrection, 능동 입력) / 미저장 변경(hasAnyChange)
  //    / 저장 진행 중 → 유실·오조작 방지.
  //  - (C) 재처리 대기(시머)·pending diff 검토(showReview) 중에도 이동 허용:
  //    이동하면 file 변경 effect 가 오버레이를 정리하고, 진행 중이던 재처리는
  //    RecordTable 의 백그라운드 watcher 가 계속 추적한다(끝나면 해당 케이스에서 검토).
  //  - 단, diff 오버레이가 떠 있고 입력요소(메모 등)에 포커스가 있으면 커서 이동 우선.
  useEffect(() => {
    const onArrow = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (showCorrection || hasAnyChange || editMutation.isPending) return;
      if (showReview) {
        const el = document.activeElement as HTMLElement | null;
        if (
          el &&
          (el.tagName === "INPUT" ||
            el.tagName === "TEXTAREA" ||
            el.tagName === "SELECT" ||
            el.isContentEditable)
        ) {
          return;
        }
      }
      if (e.key === "ArrowRight" && onNext && canNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft" && onPrev && canPrev) {
        e.preventDefault();
        onPrev();
      }
    };
    window.addEventListener("keydown", onArrow);
    return () => window.removeEventListener("keydown", onArrow);
  }, [
    showCorrection,
    showReview,
    hasAnyChange,
    editMutation.isPending,
    onPrev,
    onNext,
    canPrev,
    canNext,
  ]);

  // 반려 체크 토글. 해제(true→false) 순간, 비고(備考=remarks)에 남은 반려
  // 사유가 있으면 즉시 삭제 여부를 확인하고, 승인 시 로컬 csv 를 바로 비워
  // 화면에 즉시 반영한다(변경분으로 저장까지 이어짐). 저장 시점에만 반영해
  // 화면과 어긋나던 문제 대응.
  // (납품은 일본어판이라 사용자가 매번 수동으로 비우던 번거로움을 줄인다.)
  const handleRejectedToggle = (checked: boolean) => {
    if (readOnly) return;
    setIsRejected(checked);
    if (checked) return;
    const existingRemarks = String(csv.remarks ?? "").trim();
    if (existingRemarks && window.confirm(t("records.edit.rejection.clearRemarksConfirm"))) {
      setCsv((prev) => ({ ...prev, remarks: "" }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return; // 관리자 감독(읽기전용) — 저장 불가
    if (!hasAnyChange) return;
    if (hasInvalidAmount) return; // 금액 형식 오류 시 저장 차단

    const payload: Parameters<typeof editMutation.mutate>[0]["payload"] = {};
    if (Object.keys(changedCsv).length > 0) {
      payload.csv_data = changedCsv;
    }
    if (isRejected !== (record?.is_rejected ?? false)) {
      payload.is_rejected = isRejected;
    }
    // 반려 상태에서 사유가 바뀌었거나 새로 반려된 경우 사유 전송
    if (isRejected && rejectionReason !== (record?.rejection_reason ?? "")) {
      payload.rejection_reason = rejectionReason.trim() || null;
    }
    if (memo.trim()) {
      payload.memo = memo.trim();
    }

    // Q1=separate — paging 사용 시 (호출측이 ``onPrev`` / ``onNext`` 를
    // 주입) 저장 후 모달을 유지해 사용자가 같은 파일을 더 수정하거나 명시
    // 적으로 다음/이전으로 이동할 수 있게 한다.
    // paging 미사용인 단독 호출(레거시 호출 경로) 의 경우엔 기존 UX 를
    // 보존하기 위해 저장 후 자동으로 모달을 닫는다.
    const pagingEnabled = Boolean(onPrev || onNext);
    editMutation.mutate(
      { jobId, fileId: file.id, payload },
      {
        onSuccess: () => {
          if (!pagingEnabled) onClose();
        },
      },
    );
  };

  const apiError =
    editMutation.error instanceof ApiError
      ? editMutation.error.detail
      : editMutation.error?.message;

  const pendingExists = record?.pending_extraction != null;
  // #6 — 재추출 요청이 진행 중(터미널 이전)이면 "수정중" 상태.
  const correcting = correctionRequestId != null && !pendingExists;

  // 최근 수정제안 결과(무변경/실패)를 서버 이력에서 읽어 지속 표시.
  //  - 케이스 이동 후 돌아와도(일시 state 와 달리) 결과와 AI 사유를 확인할 수 있다.
  //  - 변경 있는 결과(pending)는 diff 검토 흐름에서 처리하므로 여기선 제외.
  const correctionListQ = useCorrectionList(jobId, file.id);
  const persistentCorrectionResult = useMemo(() => {
    if (pendingExists) return null;
    const latest = correctionListQ.data?.[0];
    if (!latest) return null;
    if (latest.status !== "completed_no_change" && latest.status !== "failed") return null;
    const rawReasoning = latest.llm_response?.reasoning;
    return {
      status: latest.status,
      reasoning: typeof rawReasoning === "string" ? rawReasoning : "",
      errorMessage: latest.error_message ?? "",
    };
  }, [correctionListQ.data, pendingExists]);

  // 수정제안 열기 — 트리거 버튼과 동일 가드(미저장 변경/pending/진행중이면 불가).
  const openCorrection = useCallback(() => {
    if (readOnly) return;
    if (!record || hasAnyChange || pendingExists || editMutation.isPending || correcting) return;
    if (showCorrection || showReview) return;
    setShowCorrection(true);
  }, [
    readOnly,
    record,
    hasAnyChange,
    pendingExists,
    editMutation.isPending,
    correcting,
    showCorrection,
    showReview,
  ]);

  // 검수 단축키(Alt 조합) — 편집창 한정.
  //  - Alt+1: 1차 검수완료 토글 / Alt+2: 2차 확정 토글 / Alt+R: 수정제안 열기.
  //  - Alt 조합이라 입력필드 타이핑 중에도 안전(숫자/문자 입력과 충돌 없음).
  //  - macOS 옵션키는 특수문자를 만들 수 있어 e.key 대신 e.code(Digit/Numpad/KeyR)로 판정.
  //  - 각 동작의 활성 조건은 해당 토글/열기 함수 내부 가드가 담당.
  useEffect(() => {
    const onHotkey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.code === "Digit1" || e.code === "Numpad1") {
        if (showCorrection || showReview) return; // 오버레이 중엔 검수 토글 미발동
        e.preventDefault();
        toggleReview();
      } else if (e.code === "Digit2" || e.code === "Numpad2") {
        if (showCorrection || showReview) return;
        e.preventDefault();
        toggleConfirm();
      } else if (e.code === "KeyR") {
        e.preventDefault();
        openCorrection();
      }
    };
    window.addEventListener("keydown", onHotkey);
    return () => window.removeEventListener("keydown", onHotkey);
  }, [showCorrection, showReview, toggleReview, toggleConfirm, openCorrection]);

  // 검토 오버레이 상태 동기화.
  //  - 채택/폐기 성공 시엔 DiffReviewPanel 이 스스로 onClose 하므로 여기서 강제
  //    닫지 않는다.
  //  - #4: 수정제안 제출 즉시 오버레이를 열어 결과 도착 전까지 스켈레톤으로 대기하므로,
  //    pending 이 아직 없다는 이유만으로 닫으면 안 된다. "보여줄 것이 아무것도 없을 때"
  //    (pending 없음 + 진행중 아님 + 종료상태 안내도 없음) 에만 정리한다.
  useEffect(() => {
    if (
      showReview &&
      !pendingExists &&
      correctionRequestId == null &&
      lastCorrectionStatus == null
    ) {
      setShowReview(false);
    }
  }, [pendingExists, showReview, correctionRequestId, lastCorrectionStatus]);

  // 파일(레코드)이 바뀌면 수정중/종료안내 상태를 초기화 (paging 대비).
  //  - 검토 오버레이는 "그 케이스에 pending 결과가 있으면 곧바로 열어" 준다.
  //    → 키보드로 이동해 도착한 케이스에 재처리 결과가 있으면 "대기중" 배너를
  //      한 번 더 누르지 않고 바로 diff 를 보여준다.
  useEffect(() => {
    setCorrectionRequestId(null);
    setLastCorrectionStatus(null);
    // 읽기전용(관리자 감독)에선 쓰기 가능한 diff 검토 오버레이를 자동으로 열지 않는다.
    setShowReview(!readOnly && file.record?.pending_extraction != null);
  }, [file.id, file.record?.pending_extraction, readOnly]);

  // 같은 케이스에 머무는 동안 재처리가 끝나 pending 이 도착하면 자동으로 검토를 연다
  // (대기 배너 클릭 불필요). pending 이 해제(채택/폐기)될 땐 여기서 닫지 않는다
  // — DiffReviewPanel 이 스스로 onClose 처리.
  useEffect(() => {
    if (pendingExists && !readOnly) setShowReview(true);
  }, [pendingExists, readOnly]);

  const handleCorrectionTerminal = useCallback((status: CorrectionStatus) => {
    setLastCorrectionStatus(status);
    setCorrectionRequestId(null);
  }, []);

  return (
    // backdrop 클릭으로는 닫지 않는다 — 드래그·드롭 중 포인터가 음영에서
    // 떨어져도 팝업이 꺼지지 않도록. 닫기는 Esc 키 / 헤더 X 버튼으로만.
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50",
        maximized ? "p-2" : "px-4 py-8",
      )}
    >
      <div
        className={cn(
          "w-full rounded-lg bg-white shadow-xl",
          // 최대화: 화면에 꼭 맞는 고정 높이 flex 컬럼 → 본문만 스크롤, 푸터 항상 노출.
          maximized ? "flex h-[calc(100vh-1rem)] max-w-none flex-col overflow-hidden" : "max-w-6xl",
        )}
      >
        {/* 헤더 */}
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">
              {t("records.edit.title")}
              {position && (
                <span
                  className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-normal tabular-nums text-slate-600"
                  aria-label={t("records.edit.positionAria", {
                    total: position.total,
                    current: position.current,
                  })}
                >
                  {position.current} / {position.total}
                </span>
              )}
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500" title={file.original_name}>
              {file.original_name}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={toggleMaximized}
              className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={
                maximized ? t("records.edit.restoreAria") : t("records.edit.maximizeAria")
              }
              title={maximized ? t("records.edit.restoreAria") : t("records.edit.maximizeAria")}
            >
              {maximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={t("common.actions.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* 관리자 감독(읽기전용) 안내 배너 */}
        {readOnly && (
          <div className="mx-5 mt-4 flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-xs text-indigo-800">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span>{t("records.edit.readOnlyBanner")}</span>
          </div>
        )}

        {/* #4 — 결과 오버레이(showReview)가 떠 있는 동안엔 뒤쪽의 "검토 대기/재검토"
            배너를 숨긴다(오버레이가 대기·결과를 모두 담당 → 중복 UI 제거).
            오버레이를 닫은 상태에서만 fallback 으로 노출. */}
        {correcting && !showReview ? (
          <div className="m-5 flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50 px-4 py-4 text-sm text-violet-800">
            <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" />
            <div>
              <p className="font-medium">{t("records.edit.correcting.title")}</p>
              <p className="mt-0.5 text-xs">{t("records.edit.correcting.body")}</p>
            </div>
          </div>
        ) : pendingExists && !showReview ? (
          <div className="m-5 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">{t("records.edit.pendingTitle")}</p>
                <p className="mt-0.5 text-xs">{t("records.edit.pendingBody")}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowReview(true)}
              disabled={readOnly}
              className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Eye className="h-3.5 w-3.5" />
              {t("records.edit.pendingReviewButton")}
            </button>
          </div>
        ) : !record ? (
          <p className="px-5 py-10 text-center text-sm text-slate-400">
            {t("records.edit.noRecord")}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className={cn(maximized && "flex min-h-0 flex-1 flex-col")}>
            {/* #6 — 최근 수정제안이 변경없음/실패로 끝난 경우 결과 + AI 사유를 지속 표시.
                (서버 이력 기반이라 키보드로 케이스 이동 후 돌아와도 유지된다) */}
            {persistentCorrectionResult && (
              <div
                className={cn(
                  "mx-5 mt-4 rounded-md border px-4 py-2.5 text-xs",
                  persistentCorrectionResult.status === "failed"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-slate-200 bg-slate-50 text-slate-600",
                )}
              >
                <div className="flex items-center gap-1.5 font-medium">
                  <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
                  {persistentCorrectionResult.status === "failed"
                    ? t("records.edit.correcting.failed")
                    : t("records.edit.correcting.noChange")}
                </div>
                {persistentCorrectionResult.status === "failed"
                  ? persistentCorrectionResult.errorMessage && (
                      <p className="mt-1 whitespace-pre-wrap">
                        {persistentCorrectionResult.errorMessage}
                      </p>
                    )
                  : persistentCorrectionResult.reasoning && (
                      <p className="mt-1 whitespace-pre-wrap text-slate-700">
                        {localizeAiText(persistentCorrectionResult.reasoning, i18n.language)}
                      </p>
                    )}
              </div>
            )}
            {/* 2-column body: 좌측 PDF 미리보기 / 가운데 splitter / 우측 편집 폼.
                lg 미만에서는 splitter 숨기고 stack 레이아웃 (모바일).
                lg 이상에서는 grid-template-columns 를 useResizableSplit 의 leftPct
                로 동적 제어 — 분할자 드래그로 사용자가 자유롭게 비율 조절. */}
            <div
              ref={split.containerRef}
              className={cn(
                "flex flex-col lg:grid",
                // 최대화: 남은 높이를 채우고(flex-1) 모바일은 세로 스크롤,
                // 데스크톱(lg)은 각 컬럼이 자체 스크롤하므로 부모는 스크롤하지 않음.
                maximized && "min-h-0 flex-1 overflow-y-auto lg:overflow-visible",
              )}
              style={{
                gridTemplateColumns: `${split.leftPct}% 6px 1fr`,
              }}
            >
              {/* ─── 좌: PDF 미리보기 패널 (toolbar + ImagePagePreview + bbox) ─── */}
              <div className="border-b border-slate-200 lg:overflow-hidden lg:border-b-0">
                <div className={maximized ? "h-[calc(100vh-9rem)] lg:h-full" : "h-[70vh]"}>
                  <PdfPreviewPanel
                    jobId={jobId}
                    fileId={file.id}
                    originalName={file.original_name}
                    bboxes={record.csv_bbox}
                    activeFieldKey={activeFieldKey}
                    hoveredFieldKey={hoveredFieldKey}
                    onBboxClick={handleBboxClick}
                    onBboxHover={(key) => setHoveredFieldKey(key)}
                  />
                </div>
              </div>

              {/* ─── 가운데: 분할자 (drag handle). 모바일에서는 숨김. ─── */}
              <div
                {...split.handleProps}
                role="separator"
                aria-orientation="vertical"
                aria-label={t("records.edit.splitterAria")}
                title={t("records.edit.splitterTitle")}
                className={cn(
                  "hidden cursor-col-resize transition-colors lg:block",
                  split.isDragging ? "bg-blue-400" : "bg-slate-200 hover:bg-blue-300",
                )}
              />

              {/* ─── 우: 편집 폼 (기존 내용) ─── */}
              <div
                className={cn("lg:overflow-auto", maximized ? "lg:max-h-full" : "lg:max-h-[70vh]")}
              >
                {/* #4 — "확인 필요" 소견 패널: 의심 신호 + 원본 불일치 + AI 소견 */}
                {hasReviewPanel && (
                  <div className="mx-5 mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
                    <button
                      type="button"
                      onClick={toggleReviewPanel}
                      aria-expanded={!reviewPanelCollapsed}
                      className="flex w-full items-center gap-1.5 text-left text-xs font-semibold text-amber-800"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>{t("records.edit.reviewPanel.title")}</span>
                      {reviewPanelCollapsed ? (
                        <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : (
                        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0" aria-hidden />
                      )}
                    </button>
                    <ul
                      className={cn(
                        "mt-2 space-y-1 text-xs text-amber-950/90",
                        reviewPanelCollapsed && "hidden",
                      )}
                    >
                      {reviewSignals.map((sig) => (
                        <li key={`sig-${sig.kind}`} className="flex gap-1.5">
                          <span aria-hidden className="text-amber-500">
                            •
                          </span>
                          <span>{t(sig.messageKey)}</span>
                        </li>
                      ))}
                      {lowConfFieldKeys.map((key) => (
                        <li key={`lc-${key}`} className="flex gap-1.5">
                          <span aria-hidden className="text-amber-500">
                            •
                          </span>
                          <span>
                            {t("records.edit.reviewPanel.lowConfItem", {
                              field: t(FIELD_LABEL_KEY_MAP[key]),
                            })}
                          </span>
                        </li>
                      ))}
                      {reviewNotes.map((note, i) => (
                        <li key={`note-${i}`} className="flex gap-1.5">
                          <span aria-hidden className="text-amber-500">
                            •
                          </span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 자동 검수가 고친 값 — 원래 값 → 고친 값, 확인하면 목록의 "확인 권장"이 꺼진다 */}
                {autoFix.length > 0 && (
                  <div
                    className={cn(
                      "mx-5 mt-4 rounded-md border px-4 py-3",
                      autoFixChecked
                        ? "border-slate-200 bg-slate-50"
                        : "border-amber-300 bg-amber-50",
                    )}
                  >
                    <p
                      className={cn(
                        "flex items-center gap-1.5 text-xs font-semibold",
                        autoFixChecked ? "text-slate-600" : "text-amber-800",
                      )}
                    >
                      {autoFixChecked ? (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : (
                        <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      )}
                      {t("records.edit.autoFix.title")}
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-700">
                      {autoFix.map((change) => {
                        const labelKey = (
                          FIELD_LABEL_KEY_MAP as Record<string, string | undefined>
                        )[change.key];
                        return (
                          <li key={change.key} className="flex flex-wrap items-baseline gap-x-1.5">
                            <span className="font-medium">
                              {labelKey ? t(labelKey) : change.key}
                            </span>
                            <span className="text-slate-500">{change.before || emptyValue}</span>
                            <span aria-hidden className="text-slate-400">
                              →
                            </span>
                            <span className="font-semibold text-slate-900">
                              {change.after || emptyValue}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] text-slate-500">
                        {autoFixChecked
                          ? t("records.edit.autoFix.checkedBy", {
                              who: record?.automation?.checked_by?.email ?? "—",
                              at: formatDateTime(record?.automation?.checked_at ?? null),
                            })
                          : t("records.edit.autoFix.help")}
                      </p>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={toggleAutoFixChecked}
                          disabled={autoFixBusy}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60",
                            autoFixChecked
                              ? "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                              : "border-amber-600 bg-amber-600 text-white hover:bg-amber-700",
                          )}
                        >
                          {autoFixBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          ) : (
                            !autoFixChecked && <CheckCircle2 className="h-3 w-3" aria-hidden />
                          )}
                          {autoFixChecked
                            ? t("records.edit.autoFix.uncheck")
                            : t("records.edit.autoFix.check")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {/* #7 — 1차 검수 완료 체크 + 최종 AI 검토 결과 */}
                {record && (
                  <div className="mx-5 mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={reviewChecked}
                          disabled={reviewBusy || readOnly}
                          onChange={toggleReview}
                          className="h-4 w-4 cursor-pointer accent-blue-600 disabled:cursor-not-allowed"
                        />
                        {t("records.review.checkbox")}
                        <kbd className="ml-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-normal text-slate-400">
                          Alt+1
                        </kbd>
                      </label>
                      <ReviewStatusBadge record={record} />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {t("records.review.checkboxHint")}
                    </p>
                    {reviewState === "running" && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-violet-700">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {t("records.review.runningBody")}
                      </p>
                    )}
                    {reviewState === "passed" && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {t("records.review.passedBody")}
                      </p>
                    )}
                    {reviewState === "needsReview" &&
                      record.ai_review &&
                      record.ai_review.issues.length > 0 && (
                        <div className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-red-800">
                            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                            {t("records.review.issuesTitle")}
                          </p>
                          <ul className="mt-1 space-y-1 text-xs text-red-900/90">
                            {record.ai_review.issues.map((iss, i) => (
                              <li key={`ai-${i}`} className="flex gap-1.5">
                                <span aria-hidden className="text-red-500">
                                  •
                                </span>
                                <span>{localizeAiText(iss, i18n.language)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    {/* AI 정상 확인 항목(긍정 확인) — verdict 무관, 있으면 표시 */}
                    {record.ai_review &&
                      record.ai_review.checks &&
                      record.ai_review.checks.length > 0 && (
                        <details className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2">
                          <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-emerald-800">
                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                            {t("records.review.checksTitle")}
                          </summary>
                          <ul className="mt-1 space-y-1 text-xs text-emerald-900/90">
                            {record.ai_review.checks.map((chk, i) => (
                              <li key={`chk-${i}`} className="flex gap-1.5">
                                <span aria-hidden className="text-emerald-500">
                                  ✓
                                </span>
                                <span>{localizeAiText(chk, i18n.language)}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    {/* 2차 검수 완료 — 1차 완료 상태에서만 노출 */}
                    {reviewChecked && (
                      <div className="mt-3 border-t border-slate-200 pt-3">
                        <label
                          className={cn(
                            "inline-flex items-center gap-2 text-sm font-medium",
                            confirmAvailable
                              ? "cursor-pointer text-slate-700"
                              : "cursor-not-allowed text-slate-400",
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={confirmChecked}
                            disabled={confirmBusy || !confirmAvailable || readOnly}
                            onChange={toggleConfirm}
                            className="h-4 w-4 accent-blue-600 disabled:cursor-not-allowed"
                          />
                          {t("records.review.confirmCheckbox")}
                          <kbd className="ml-1 rounded border border-slate-300 bg-white px-1 py-0.5 text-[10px] font-normal text-slate-400">
                            Alt+2
                          </kbd>
                        </label>
                        <p className="mt-1 text-[11px] text-slate-500">
                          {confirmAvailable
                            ? t("records.review.confirmHint")
                            : t("records.review.confirmDisabledHint")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* 필드 그리드 */}
                <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
                  {EDITABLE_FIELDS.map((meta) => {
                    const value = csv[meta.key];
                    const changed = value !== initialCsv[meta.key];
                    const autoFixed = autoFixByKey.get(meta.key);
                    const isActive = activeFieldKey === meta.key;
                    const isHovered = hoveredFieldKey === meta.key;
                    // #2: bbox 매칭을 수행한 필드 중 "정확일치(confidence===1.0)" 가
                    // 아니면 저신뢰로 간주해 붉은 점 표시.
                    //   - null           → PDF 원문에서 미발견
                    //   - confidence < 1 → 퍼지 매칭(값과 원문 표기가 다름 = 오타 의심)
                    // (effectiveBboxes 자체가 null 이면 OCR/매칭 미수행 → 배지 미표시)
                    const bboxEntry =
                      effectiveBboxes !== null &&
                      Object.prototype.hasOwnProperty.call(effectiveBboxes, meta.key)
                        ? effectiveBboxes[meta.key]
                        : undefined;
                    const lowConfidence =
                      meta.key !== "remarks" &&
                      bboxEntry !== undefined &&
                      (bboxEntry === null || bboxEntry.confidence < 1);
                    // #3 — 값 기반 신호(빈값/불일치). 있으면 강조(붉은 테두리) + 사유 툴팁.
                    const signalKey = signalFieldMap.get(meta.key);
                    // 금액 형식 오류(REQ-014) — 콤마/문자 등. 저장 차단 대상.
                    const invalidAmount = invalidAmountKeys.includes(meta.key);
                    const needsCheck = lowConfidence || signalKey != null || invalidAmount;
                    const dotTitle = invalidAmount
                      ? t("records.edit.amountInvalidHint")
                      : signalKey
                        ? t(signalKey)
                        : t("records.edit.matchFailedTitle");
                    const dotAria = invalidAmount
                      ? t("records.edit.amountInvalidHint")
                      : signalKey
                        ? t(signalKey)
                        : t("records.edit.matchFailedAria");
                    return (
                      <label
                        key={meta.key}
                        onMouseEnter={() => setHoveredFieldKey(meta.key)}
                        onMouseLeave={() => setHoveredFieldKey(null)}
                        className={cn(
                          "block rounded-md px-1.5 py-1 text-xs text-slate-600 transition-colors",
                          isActive
                            ? "bg-blue-50 ring-1 ring-blue-300"
                            : isHovered
                              ? "bg-blue-50/50"
                              : "",
                        )}
                      >
                        <span className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-1.5">
                            <span>{t(meta.labelI18nKey)}</span>
                            {needsCheck && (
                              <span
                                role="img"
                                aria-label={dotAria}
                                title={dotTitle}
                                className="inline-block h-2 w-2 rounded-full bg-red-500"
                              />
                            )}
                          </span>
                          {changed ? (
                            <span className="text-[10px] font-medium text-blue-600">
                              {t("records.edit.changedBadge")}
                            </span>
                          ) : (
                            autoFixed && (
                              <span
                                className={cn(
                                  "text-[10px] font-medium",
                                  autoFixChecked ? "text-slate-500" : "text-amber-700",
                                )}
                              >
                                {t("records.edit.autoFix.fieldTag")}
                              </span>
                            )
                          )}
                        </span>
                        <input
                          ref={(el) => {
                            inputRefs.current.set(meta.key, el);
                          }}
                          type={meta.type === "date" ? "text" : "text"}
                          value={value}
                          disabled={readOnly}
                          onChange={(e) => setCsv((p) => ({ ...p, [meta.key]: e.target.value }))}
                          onFocus={() => setActiveFieldKey(meta.key)}
                          placeholder={meta.hintI18nKey ? t(meta.hintI18nKey) : undefined}
                          className={cn(
                            "mt-1 w-full rounded-md border px-2 py-1.5 text-sm text-slate-900 transition disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500",
                            "focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200",
                            // 우선순위: 금액형식오류(빨강 강조) > 변경됨(파랑) > 값 신호(빨강)
                            //   > 자동 검수가 고치고 아직 확인 전(주황) > 기본
                            invalidAmount
                              ? "border-red-400 bg-red-50"
                              : changed
                                ? "border-blue-300 bg-blue-50/40"
                                : signalKey != null
                                  ? "border-red-300 bg-red-50/40"
                                  : autoFixed && !autoFixChecked
                                    ? "border-amber-300 bg-amber-50/40"
                                    : "border-slate-200",
                            meta.type === "amount" && "text-right tabular-nums",
                          )}
                        />
                        {invalidAmount && (
                          <p className="mt-1 text-[11px] font-medium text-red-600">
                            {t("records.edit.amountInvalidHint")}
                          </p>
                        )}
                        {autoFixed && !changed && (
                          <p className="mt-1 text-[11px] text-slate-500">
                            {t("records.edit.autoFix.original", {
                              value: autoFixed.before || emptyValue,
                            })}
                          </p>
                        )}
                        {meta.key === "agent_name" && record?.delivered_agent_name && !changed && (
                          <p className="mt-1 text-[11px] text-slate-500">
                            {t("records.edit.deliveredAgentNameHint", {
                              stored: toStr(record.csv_data?.agent_name),
                            })}
                          </p>
                        )}
                      </label>
                    );
                  })}
                </div>

                {/* 반려 영역 */}
                <fieldset className="mx-5 mb-4 rounded-md border border-slate-200 px-4 py-3">
                  <legend className="px-1 text-xs font-medium text-slate-500">
                    {t("records.edit.rejection.legend")}
                  </legend>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={isRejected}
                      disabled={readOnly}
                      onChange={(e) => handleRejectedToggle(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-400 disabled:cursor-not-allowed"
                    />
                    {t("records.edit.rejection.checkboxLabel")}
                  </label>
                  {isRejected && (
                    <textarea
                      value={rejectionReason}
                      disabled={readOnly}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder={t("records.edit.rejection.reasonPlaceholder")}
                      rows={2}
                      className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  )}
                </fieldset>

                {/* 메모 */}
                <div className="mx-5 mb-4">
                  <label className="block text-xs text-slate-600">
                    <span>{t("records.edit.memo.label")}</span>
                    <input
                      type="text"
                      value={memo}
                      disabled={readOnly}
                      onChange={(e) => setMemo(e.target.value)}
                      maxLength={500}
                      placeholder={t("records.edit.memo.placeholder")}
                      className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </label>
                </div>

                {/* 이력 (있을 때만) */}
                {record.edits.length > 0 && (
                  <details className="mx-5 mb-4 rounded-md border border-slate-200 px-3 py-2 text-xs">
                    <summary className="cursor-pointer text-slate-600">
                      {t("records.edit.history.summary", { count: record.edits.length })}
                    </summary>
                    <ul className="mt-2 space-y-2">
                      {record.edits
                        .slice()
                        .reverse()
                        .map((e, i) => (
                          <li key={i} className="rounded bg-slate-50 px-2 py-1.5 text-slate-600">
                            <div className="flex justify-between text-[11px] text-slate-400">
                              <span>
                                {e.actor === "auto_review"
                                  ? t("records.automation.actor")
                                  : `${e.action ?? "edit"} · user#${e.user_id}`}
                              </span>
                              <span>{new Date(e.timestamp).toLocaleString()}</span>
                            </div>
                            {e.memo && <p className="mt-0.5 italic text-slate-500">"{e.memo}"</p>}
                            {Object.keys(e.after).length > 0 && (
                              <ul className="mt-1 space-y-0.5 text-[11px]">
                                {Object.keys(e.after).map((k) => (
                                  <li key={k} className="flex gap-2">
                                    <span className="font-medium text-slate-500">{k}:</span>
                                    <span className="text-slate-400 line-through">
                                      {toStr(e.before[k])}
                                    </span>
                                    <span>→</span>
                                    <span className="text-slate-700">{toStr(e.after[k])}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                    </ul>
                  </details>
                )}

                {/* 오류 */}
                {apiError && (
                  <div className="mx-5 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {apiError}
                  </div>
                )}
              </div>
              {/* /우: 편집 폼 */}
            </div>
            {/* /2-column body */}

            {/* 버튼 — 최대화 시 flex 컬럼의 하단에 고정(shrink-0)되어 항상 노출. */}
            <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              {/*
               * 좌측 — paging 컨트롤 (Q1=separate, Q2=block_with_hint).
               *
               * - paging props (``onPrev`` / ``onNext``) 가 전달되지 않은 경우
               *   (예: 호출측이 paging 미사용) 자리 자체를 비워둔다 (빈 div
               *   는 ``justify-between`` 의 좌측 자리를 차지하기 위한 placeholder).
               * - 변경사항이 있으면 (``hasAnyChange``) 양쪽 모두 비활성화 +
               *   툴팁으로 '저장/취소가 먼저' 임을 안내.
               * - 첫/마지막 파일이면 해당 방향만 비활성화 (Q4=disable).
               */}
              {onPrev || onNext ? (
                <div
                  className="flex items-center gap-1.5"
                  role="group"
                  aria-label={t("records.edit.paging.groupAria")}
                >
                  <button
                    type="button"
                    onClick={onPrev}
                    disabled={!onPrev || !canPrev || hasAnyChange || editMutation.isPending}
                    title={
                      hasAnyChange
                        ? t("records.edit.paging.blockedTitle")
                        : !canPrev
                          ? t("records.edit.paging.firstTitle")
                          : t("records.edit.paging.prevTitle")
                    }
                    aria-label={t("records.edit.paging.prevAria")}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {t("records.edit.paging.prev")}
                  </button>
                  <button
                    type="button"
                    onClick={onNext}
                    disabled={!onNext || !canNext || hasAnyChange || editMutation.isPending}
                    title={
                      hasAnyChange
                        ? t("records.edit.paging.blockedTitle")
                        : !canNext
                          ? t("records.edit.paging.lastTitle")
                          : t("records.edit.paging.nextTitle")
                    }
                    aria-label={t("records.edit.paging.nextAria")}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t("records.edit.paging.next")}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div />
              )}

              {/* 우측 — 수정제안 / 저장 / 취소 (읽기전용이면 '닫기'만 노출) */}
              <div className="flex items-center gap-2">
                {readOnly ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
                  >
                    {t("common.actions.close")}
                  </button>
                ) : (
                  <>
                    {/*
                     * 수정제안 (M15) — 자연어 + 시각 표시로 vision 재추출.
                     * 차단: 편집 변경값이 있으면(저장/취소 먼저) / pending 검토 대기 중이면.
                     */}
                    <button
                      type="button"
                      onClick={() => setShowCorrection(true)}
                      disabled={hasAnyChange || pendingExists || editMutation.isPending}
                      title={
                        hasAnyChange
                          ? t("corrections.trigger.blockedByChange")
                          : pendingExists
                            ? t("corrections.trigger.blockedByPending")
                            : t("corrections.trigger.title")
                      }
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition",
                        hasAnyChange || pendingExists || editMutation.isPending
                          ? "cursor-not-allowed border-slate-200 text-slate-300"
                          : "border-violet-300 text-violet-700 hover:bg-violet-50",
                      )}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {t("corrections.trigger.button")}
                      <kbd className="ml-0.5 rounded border border-current px-1 py-0.5 text-[10px] font-normal opacity-50">
                        Alt+R
                      </kbd>
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
                    >
                      {t("common.actions.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={!hasAnyChange || hasInvalidAmount || editMutation.isPending}
                      title={hasInvalidAmount ? t("records.edit.amountInvalidHint") : undefined}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition",
                        hasAnyChange && !hasInvalidAmount && !editMutation.isPending
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "cursor-not-allowed bg-slate-300",
                      )}
                    >
                      {editMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                      {t("common.actions.save")}
                    </button>
                  </>
                )}
              </div>
            </footer>
          </form>
        )}
      </div>

      {/* M15 — 수정제안 모달 (이 편집 모달 위에 오버레이) */}
      {showCorrection && (
        <CorrectionSuggestionModal
          jobId={jobId}
          file={file}
          maximized={maximized}
          onClose={() => setShowCorrection(false)}
          onSubmitted={(fileId, requestId) => {
            // #6 — 편집 모달을 닫지 않고 "수정중" 상태로 전환. RecordTable 행
            // 진행 표시도 함께 갱신(모달을 닫아도 목록에서 진행이 보이도록).
            onCorrectionSubmitted?.(fileId, requestId);
            setShowCorrection(false);
            setLastCorrectionStatus(null);
            setCorrectionRequestId(requestId);
            // 결과 오버레이를 즉시 연다 → 결과 도착 전까지 스켈레톤(시머)으로 대기.
            // (기존: 완료 후 노란 배너의 "결과 검토" 버튼을 한 번 더 눌러야 했던 2스텝 제거)
            setShowReview(true);
          }}
        />
      )}

      {/* #6 — 진행 중 재추출 상태 폴링 (UI 없음). 터미널 도달 시 상태 갱신. */}
      {correctionRequestId != null && (
        <CorrectionInFlightWatcher
          jobId={jobId}
          fileId={file.id}
          requestId={correctionRequestId}
          onTerminal={handleCorrectionTerminal}
        />
      )}

      {/* #6/#4 — 재처리 결과(diff) 검토 오버레이 (편집 모달 위에 표시).
          제출 즉시 열려서 결과 도착 전까지 스켈레톤(시머)으로 대기한다. */}
      {showReview && (
        <DiffReviewPanel
          jobId={jobId}
          file={file}
          onClose={() => setShowReview(false)}
          inFlight={correcting}
          terminalStatus={lastCorrectionStatus}
          maximized={maximized}
        />
      )}
    </div>
  );
}

/** 진행 중 재추출 1건을 폴링하다 터미널 상태에 도달하면 onTerminal 호출 (UI 없음). */
function CorrectionInFlightWatcher({
  jobId,
  fileId,
  requestId,
  onTerminal,
}: {
  jobId: string;
  fileId: string;
  requestId: string;
  onTerminal: (status: CorrectionStatus) => void;
}) {
  useCorrectionStatusWatcher(jobId, fileId, requestId, onTerminal);
  return null;
}
